import { useCallback, useMemo, useState } from "react"
import type { ConditionGroup, PipelineOpportunitiesResponse } from "@/schema"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import {
  fetchBusinessOpportunities,
  fetchContactOpportunities,
  fetchHouseholdOpportunities,
  fetchOpportunities,
  fetchOpportunity,
} from "./api"

export function useFetchOpportunities(
  pipelineId: string,
  options?: {
    enabled?: boolean
    conditionGroup?: ConditionGroup
    viewSort?: { field?: string; direction?: "asc" | "desc" }
    bookIdOverride?: string
    focusOpportunityId?: string | null
    searchTerm?: string
    sumFieldKey?: string
  }
) {
  const {
    enabled = true,
    conditionGroup,
    viewSort,
    bookIdOverride,
    focusOpportunityId,
    searchTerm,
    sumFieldKey,
  } = options ?? {}
  const urlBookId = useBookId()
  const bookId = bookIdOverride ?? urlBookId
  const getContext = useApiContext(bookIdOverride)
  const queryClient = useQueryClient()
  const [fetchingStageIds, setFetchingStageIds] = useState<Set<string>>(new Set())

  const conditionGroupKey = JSON.stringify(conditionGroup)
  const viewSortKey = JSON.stringify(viewSort)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableConditionGroup = useMemo(() => conditionGroup, [conditionGroupKey])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableViewSort = useMemo(() => viewSort, [viewSortKey])

  const queryKey = useMemo(
    () => [
      "pipeline",
      pipelineId,
      "opportunities",
      "by-stage",
      bookId,
      conditionGroupKey,
      viewSortKey,
      focusOpportunityId,
      searchTerm,
      sumFieldKey,
    ],
    [pipelineId, bookId, conditionGroupKey, viewSortKey, focusOpportunityId, searchTerm, sumFieldKey]
  )

  const query = useQuery<PipelineOpportunitiesResponse>({
    queryKey,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return fetchOpportunities(
        context,
        pipelineId,
        stableConditionGroup,
        stableViewSort,
        focusOpportunityId,
        undefined,
        searchTerm,
        sumFieldKey
      )
    },
    enabled: enabled && !!pipelineId,
  })

  const fetchNextPage = useCallback(
    async (pipelineStageId: string) => {
      const currentData = queryClient.getQueryData<PipelineOpportunitiesResponse>(queryKey)
      const nextPageParams = currentData?.pagination[pipelineStageId]?.next_page_params
      if (!currentData || !nextPageParams || fetchingStageIds.has(pipelineStageId)) return

      setFetchingStageIds((current) => new Set(current).add(pipelineStageId))

      try {
        const context = await getContext()
        if (!context) throw new Error("No context")

        const nextPage = await fetchOpportunities(
          context,
          pipelineId,
          stableConditionGroup,
          stableViewSort,
          focusOpportunityId,
          nextPageParams,
          searchTerm,
          sumFieldKey,
          pipelineStageId
        )

        queryClient.setQueryData<PipelineOpportunitiesResponse>(queryKey, (latestData) => {
          if (!latestData) return latestData

          const loadedIds = new Set(latestData.opportunities.map((opportunity) => opportunity.id))
          const newOpportunities = nextPage.opportunities.filter((opportunity) => !loadedIds.has(opportunity.id))

          return {
            ...latestData,
            opportunities: [...latestData.opportunities, ...newOpportunities],
            pagination: { ...latestData.pagination, ...nextPage.pagination },
          }
        })
      } finally {
        setFetchingStageIds((current) => {
          const updated = new Set(current)
          updated.delete(pipelineStageId)
          return updated
        })
      }
    },
    [
      fetchingStageIds,
      focusOpportunityId,
      getContext,
      pipelineId,
      queryClient,
      queryKey,
      searchTerm,
      stableConditionGroup,
      stableViewSort,
      sumFieldKey,
    ]
  )

  const hasNextPage = useCallback(
    (pipelineStageId: string) => !!query.data?.pagination[pipelineStageId]?.next_page_params,
    [query.data?.pagination]
  )
  const isFetchingNextPage = useCallback(
    (pipelineStageId: string) => fetchingStageIds.has(pipelineStageId),
    [fetchingStageIds]
  )

  return { ...query, fetchNextPage, hasNextPage, isFetchingNextPage }
}

export function useFetchOpportunity(pipelineId: string, opportunityId: string, enabled = true) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["pipeline", pipelineId, "opportunities", opportunityId],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return fetchOpportunity(context, pipelineId, opportunityId)
    },
    enabled: enabled && !!pipelineId && !!opportunityId,
  })
}

export function useFetchHouseholdOpportunities(householdId: string, enabled = true) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["household", householdId, "opportunities"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return fetchHouseholdOpportunities(context, householdId)
    },
    enabled: enabled && !!householdId,
  })
}

export function useFetchContactOpportunities(contactId: string, enabled = true) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["contact", contactId, "opportunities"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return fetchContactOpportunities(context, contactId)
    },
    enabled: enabled && !!contactId,
  })
}

export function useFetchBusinessOpportunities(businessId: string, enabled = true) {
  const getContext = useApiContext()

  return useQuery({
    queryKey: ["business", businessId, "opportunities"],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context")
      return fetchBusinessOpportunities(context, businessId)
    },
    enabled: enabled && !!businessId,
  })
}
